import {useAddNotification, useTransition} from '@logistics-frontend/blocks'
import type {ServerError} from '@logistics-frontend/core/utils/network'
import {getErrorMessage} from '@logistics-frontend/utils'
import {useCallback} from 'react'

import {fetchCardTrustBindingUrl} from '../api'
import type {CardScope} from '../types'
import {useConfigContext} from '../ui'

export function useFetchCardTrustBindingUrlTransition(cardScope: CardScope['kind']) {
  const {clientId, trustDomain, country, makeUrl} = useConfigContext()
  const [{payload, isPending, error}] = useTransition<string, ServerError>(
    () => {
      return fetchCardTrustBindingUrl(
        clientId,
        {
          card_scope: {
            kind: cardScope,
          },
          trust_domain: trustDomain,
        },
        country,
        makeUrl,
      )
    },
    {
      key: TRANSITION_KEY,
    },
  )

  return {
    cardTrustBindingUrl: payload,
    isCardTrustBindingUrlPending: isPending,
    cardTrustBindingUrlError: error,
  }
}

export function useFetchCardTrustBindingUrl() {
  const addNotification = useAddNotification()
  const {clientId, trustDomain, country, makeUrl} = useConfigContext()
  const [{payload, isPending, error}, startTransition] = useTransition<string, ServerError>(undefined, {
    key: TRANSITION_KEY,
  })

  const fetchCardTrustBindingUrlHandler = useCallback(
    (cardScope: CardScope['kind']) => {
      startTransition(
        () => {
          return fetchCardTrustBindingUrl(
            clientId,
            {
              card_scope: {
                kind: cardScope,
              },
              trust_domain: trustDomain,
            },
            country,
            makeUrl,
          )
        },
        {
          onError(error) {
            addNotification(getErrorMessage(error), {
              type: 'error',
            })
          },
        },
      )
    },
    [addNotification, clientId, country, makeUrl, trustDomain],
  )

  return {
    cardTrustBindingUrl: payload,
    isCardTrustBindingUrlPending: isPending,
    cardTrustBindingUrlError: error,
    fetchCardTrustBindingUrlHandler,
  }
}

const TRANSITION_KEY = 'fetchCardTrustBindingUrl'
